// src/layouts/Topbar.tsx
// Fixed top header — page title, search, actions, user avatar, and In-App Notification Bell popover.
import { useState, useRef, useEffect, useMemo } from 'react'
import { Bell, Sun, Moon, CheckCheck, ArrowRight, Trash2, Volume2, VolumeX } from 'lucide-react'
import { useAuth } from '@/app/hooks'
import { useGetProfileQuery } from '@/features/auth/auth.api'
import { useNavigate } from 'react-router-dom'
import {
  useGetAppUnreadCountQuery,
  useGetAppNotificationsQuery,
  useMarkAppNotificationAsReadMutation,
  useMarkAllAppNotificationsAsReadMutation,
  useDeleteAppNotificationMutation,
  useClearAllAppNotificationsMutation,
} from '@/features/notifications/notifications.api'
import ConfirmModal from '@/components/ui/ConfirmModal'
import { resolveNotificationLink, playNotificationSound } from '@/utils/notification.utils'

interface TopbarProps {
  title: string
  subtitle: string
  theme: 'light' | 'dark'
  onToggleTheme: (e: React.MouseEvent<HTMLButtonElement>) => void
}

// Custom Avatar component with initials and a linear-gradient background matching the demo
export function AvatarK({ name, color = '#9966ff', size = 32 }: { name: string; color?: string; size?: number }) {
  const ini = name
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

  const shade = (hex: string, p: number) => {
    if (!hex || hex[0] !== '#') return hex
    const n = parseInt(hex.slice(1), 16)
    let r = (n >> 16) & 255
    let g = (n >> 8) & 255
    let b = n & 255
    r = Math.max(0, Math.min(255, r + p))
    g = Math.max(0, Math.min(255, g + p))
    b = Math.max(0, Math.min(255, b + p))
    return `rgb(${r},${g},${b})`
  }

  return (
    <div
      className="grid place-items-center rounded-full shrink-0 text-white font-bold"
      style={{
        width: size,
        height: size,
        background: `linear-gradient(135deg, ${color}, ${shade(color, -28)})`,
        fontSize: size * 0.38,
        fontFamily: 'var(--fs-display)',
      }}
    >
      {ini}
    </div>
  )
}

export default function Topbar({ title, subtitle, theme, onToggleTheme }: TopbarProps) {
  const { user } = useAuth()
  const { data: profileResponse } = useGetProfileQuery(undefined, { skip: !user })
  const profile = profileResponse?.data
  const navigate = useNavigate()

  const userName = useMemo(() => {
    if (profile?.firstName) {
      return `${profile.firstName}${profile.lastName ? ' ' + profile.lastName : ''}`.trim()
    }
    if (user?.fullName) return user.fullName
    if (user?.firstName) {
      return `${user.firstName}${user.lastName ? ' ' + user.lastName : ''}`.trim()
    }
    if (user?.email) {
      const prefix = user.email.split('@')[0] ?? ''
      return prefix.replace(/[._-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    }
    return 'Staff'
  }, [profile, user])
  const userAvatarColor = '#9966ff'

  // Mute state
  const [isMuted, setIsMuted] = useState(() => {
    if (typeof window === 'undefined') return false
    return localStorage.getItem('notification_muted') === 'true'
  })

  const toggleMute = () => {
    const nextMuted = !isMuted
    setIsMuted(nextMuted)
    localStorage.setItem('notification_muted', String(nextMuted))
    if (!nextMuted) {
      playNotificationSound()
    }
  }

  // Notifications state & API queries
  const [isNotifOpen, setIsNotifOpen] = useState(false)
  const popoverRef = useRef<HTMLDivElement>(null)

  const { data: unreadCountData } = useGetAppUnreadCountQuery(undefined, { pollingInterval: 30000 })
  const {
    data: appNotifData,
    isLoading: isNotifLoading,
    isFetching: isNotifFetching,
  } = useGetAppNotificationsQuery({ page: 0, size: 5 }, { pollingInterval: 60000 })
  const [markAllAppAsRead, { isLoading: isMarkingAll }] = useMarkAllAppNotificationsAsReadMutation()
  const [clearAllAppNotifications, { isLoading: isClearingAll }] = useClearAllAppNotificationsMutation()
  const [markAppAsRead] = useMarkAppNotificationAsReadMutation()
  const [deleteAppNotification] = useDeleteAppNotificationMutation()

  const unreadCount = typeof unreadCountData === 'number' ? unreadCountData : 0
  const recentNotifications = appNotifData?.content || []

  // Play sound when unread count increases
  const prevUnreadRef = useRef(unreadCount)
  useEffect(() => {
    if (unreadCount > prevUnreadRef.current) {
      playNotificationSound()
    }
    prevUnreadRef.current = unreadCount
  }, [unreadCount])

  // Close popover on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setIsNotifOpen(false)
      }
    }
    if (isNotifOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isNotifOpen])

  const handleNotificationClick = (
    id: string | number,
    actionLink?: string | null,
    title?: string,
    category?: string
  ) => {
    setIsNotifOpen(false)
    markAppAsRead(id)
    const target = resolveNotificationLink(actionLink, title, category)
    navigate(target)
  }

  const handleDelete = async (e: React.MouseEvent, id: string | number) => {
    e.stopPropagation()
    try {
      await deleteAppNotification(id).unwrap()
    } catch {
      // Handled
    }
  }

  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false)

  const handleClearAll = () => {
    setIsClearConfirmOpen(true)
  }

  const handleConfirmClearAll = async () => {
    try {
      await clearAllAppNotifications().unwrap()
    } catch {
      // Handled
    }
  }

  return (
    <header className="flex items-center gap-2 md:gap-4 pl-[70px] pr-3 sm:pr-6 md:pr-[26px] lg:px-[26px] py-4 shrink-0 z-[4] relative">
      {/* ── Title block — hidden on mobile ──────────────────────── */}
      <div className="flex-1 min-w-0 hidden sm:block">
        <h1 className="t-display text-[19px] sm:text-[23px] font-extrabold leading-[1.1] text-[var(--txt)] truncate">
          {title}
        </h1>
        {subtitle && (
          <div className="text-[11px] sm:text-[13px] text-[var(--txt-2)] mt-0.5 truncate">
            {subtitle}
          </div>
        )}
      </div>

      {/* ── Dark/Light mode toggle — visible on all screens ── */}
      <button
        onClick={onToggleTheme}
        className="tap grid w-10 h-10 rounded-xl shrink-0 place-items-center text-[var(--txt-2)] relative overflow-hidden bg-[var(--glass)] border border-[var(--glass-brd)] cursor-pointer hover:text-[var(--txt)] transition-colors"
        title="Toggle theme"
      >
        {theme === 'dark' ? (
          <Sun size={19} style={{ strokeWidth: '1.85px' }} />
        ) : (
          <Moon size={19} style={{ strokeWidth: '1.85px' }} />
        )}
      </button>

      {/* ── Notification bell & Popover drawer ─────────────────── */}
      <div className="relative" ref={popoverRef}>
        <button
          onClick={() => setIsNotifOpen((prev) => !prev)}
          className="tap w-10 h-10 rounded-xl shrink-0 grid place-items-center relative text-[var(--txt-2)] bg-[var(--glass)] border border-[var(--glass-brd)] cursor-pointer hover:text-[var(--txt)] transition-colors"
          title="Notifications"
        >
          <Bell size={19} style={{ strokeWidth: '1.85px' }} />
          {unreadCount > 0 && (
            <span className="absolute top-1.5 right-1.5 flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9.5px] font-black text-white bg-[var(--rose)] shadow-[0_0_0_2px_var(--bg-0)] animate-pulse">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        {/* Popover Drawer */}
        {isNotifOpen && (
          <div className="absolute right-0 mt-2 w-84 sm:w-[420px] rounded-2xl border border-[var(--glass-brd)] bg-[var(--bg-1)] shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--glass-brd)] bg-[var(--glass)] gap-2">
              {/* Left: Title & unread badge */}
              <div className="flex items-center gap-2 min-w-0 shrink-0">
                <span className="font-extrabold text-xs sm:text-sm text-[var(--txt)] whitespace-nowrap">
                  In-App Notifications
                </span>
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-[var(--rose)]/15 text-[var(--rose)] whitespace-nowrap">
                    {unreadCount} unread
                  </span>
                )}
              </div>

              {/* Center: Sound Mute Toggle */}
              <button
                onClick={toggleMute}
                className="px-2 py-1 rounded-lg hover:bg-[var(--glass-brd)] text-[var(--txt-3)] hover:text-[var(--txt)] transition-all cursor-pointer flex items-center justify-center gap-1 border border-[var(--glass-brd)] bg-[var(--bg-2)] shadow-xs shrink-0"
                title={isMuted ? 'Unmute notification sound' : 'Mute notification sound'}
              >
                {isMuted ? (
                  <VolumeX size={13} className="text-[var(--txt-3)]" />
                ) : (
                  <Volume2 size={13} className="text-[var(--mint-500)]" />
                )}
              </button>

              {/* Right: Actions */}
              {recentNotifications.length > 0 && (
                <div className="flex items-center gap-2.5 shrink-0 ml-auto">
                  <button
                    onClick={() => markAllAppAsRead()}
                    disabled={isMarkingAll}
                    className="flex items-center gap-1 text-[11px] font-bold text-[var(--mint-500)] hover:underline disabled:opacity-50 cursor-pointer whitespace-nowrap"
                    title="Mark all as read"
                  >
                    <CheckCheck size={13} />
                    <span>Mark read</span>
                  </button>
                  <button
                    onClick={handleClearAll}
                    disabled={isClearingAll}
                    className="flex items-center gap-1 text-[11px] font-bold text-[var(--rose)] hover:underline disabled:opacity-50 cursor-pointer whitespace-nowrap"
                    title="Clear all notifications"
                  >
                    <span>Clear all</span>
                  </button>
                </div>
              )}
            </div>

            {/* List Body */}
            <div className="max-h-80 overflow-y-auto divide-y divide-[var(--glass-brd)]">
              {isNotifLoading || (isNotifFetching && !appNotifData) ? (
                <div className="divide-y divide-[var(--glass-brd)]">
                  {Array.from({ length: 3 }).map((_, idx) => (
                    <div key={idx} className="p-3.5 flex gap-3 animate-pulse">
                      <div className="w-2.5 h-2.5 rounded-full mt-1.5 bg-[var(--skeleton)] shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3.5 bg-[var(--skeleton)] rounded w-3/4" />
                        <div className="h-3 bg-[var(--skeleton)] rounded w-full" />
                        <div className="h-2.5 bg-[var(--skeleton)] rounded w-1/3" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : recentNotifications.length === 0 ? (
                <div className="p-6 text-center text-xs text-[var(--txt-3)] font-semibold">
                  <Bell size={24} className="mx-auto mb-2 opacity-40" />
                  No notifications found.
                </div>
              ) : (
                recentNotifications.map((notif) => (
                  <div
                    key={notif.id}
                    onClick={() => handleNotificationClick(notif.id, notif.actionLink, notif.title, notif.category)}
                    className={`p-3.5 hover:bg-[var(--glass-brd)]/50 transition-colors cursor-pointer flex gap-3 group relative ${
                      !notif.isRead ? 'bg-[var(--mint-500)]/5' : ''
                    }`}
                  >
                    <div
                      className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                        !notif.isRead ? 'bg-[var(--rose)]' : 'bg-transparent'
                      }`}
                    />
                    <div className="flex-1 min-w-0 pr-6">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-extrabold text-xs text-[var(--txt)] truncate">{notif.title}</span>
                      </div>
                      <p className="text-[11.5px] text-[var(--txt-3)] line-clamp-2 mt-0.5 leading-snug">
                        {notif.message}
                      </p>
                      {notif.createdAt && (
                        <div className="text-[10px] text-[var(--txt-3)] mt-1">
                          {new Date(notif.createdAt).toLocaleDateString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={(e) => handleDelete(e, notif.id)}
                      className="absolute right-2 top-3 opacity-0 group-hover:opacity-100 p-1 text-[var(--txt-3)] hover:text-[var(--rose)] transition-all"
                      title="Dismiss notification"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Footer View All Link */}
            <div className="p-2.5 border-t border-[var(--glass-brd)] bg-[var(--glass)] text-center">
              <button
                onClick={() => {
                  setIsNotifOpen(false)
                  navigate('/notifications')
                }}
                className="w-full py-1.5 rounded-xl text-xs font-black text-[var(--mint-500)] hover:bg-[var(--mint-500)]/10 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
              >
                <span>View All Notifications</span>
                <ArrowRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── User profile pill — avatar-only on mobile, full on md+ */}
      <button
        onClick={() => navigate('/profile')}
        className="tap flex items-center gap-[9px] pl-[6px] pr-3 py-[5px] sm:pr-3 rounded-full bg-[var(--glass)] border border-[var(--glass-brd)] cursor-pointer text-inherit shrink-0 hover:border-[var(--mint-500)]/40 transition-colors"
      >
        <AvatarK name={userName} color={userAvatarColor} size={32} />
        <div className="text-left pointer-events-none hidden md:block">
          <div className="text-[12.5px] font-bold leading-[1.1] text-[var(--txt)] capitalize">
            {userName}
          </div>
        </div>
      </button>

      {/* Clear All Confirmation Modal */}
      <ConfirmModal
        isOpen={isClearConfirmOpen}
        title="Clear All Notifications?"
        description="Are you sure you want to clear all in-app notifications? This action cannot be undone."
        confirmText="Clear All"
        cancelText="Keep Notifications"
        variant="danger"
        isLoading={isClearingAll}
        onConfirm={handleConfirmClearAll}
        onClose={() => setIsClearConfirmOpen(false)}
      />
    </header>
  )
}
